import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { defineCustomElements } from '@om-design-system/web-components/loader';
import apiClient from '../../utils/apiClient';
import OmButton from '../../components/ui/OmButton';
import OmCard from '../../components/ui/OmCard';
import OmAlert from '../../components/ui/OmAlert';
import OmLinearProgress from '../../components/ui/OmLinearProgress';
import OmPagination from '../../components/ui/OmPagination';
import BackButton from '../../components/ui/BackButton';
import { useEnhancedToast } from '../../contexts/EnhancedToastContext';

const SlamsSyncManagement = () => {
  const { token } = useSelector((state) => state.auth);
  const { showSuccess, showError, showWarning, showInfo, showLoading, removeToast, updateToast } = useEnhancedToast();
  
  // State management
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSyncToasts, setActiveSyncToasts] = useState(new Map());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  /** When progress/currentEndpoint do not change while sync is active (e.g. old backend), show indeterminate bar */
  const [progressPossiblyStale, setProgressPossiblyStale] = useState(false);
  const progressStuckRef = useRef({ key: null, since: null });

  // SLAMS endpoints configuration 
  const slamEndpoints = [
    { key: 'productClasses', name: 'Product Classes' },
    { key: 'currencyTypes', name: 'Currency Types' },
    { key: 'occupationClasses', name: 'Occupation Classes' },
    { key: 'clientTypes', name: 'Client Types' },
    { key: 'paymentModes', name: 'Payment Modes' },
    { key: 'relationshipTypes', name: 'Relationship Types' },
    { key: 'identityTypes', name: 'Identity Types' },
    { key: 'insurancePackages', name: 'Insurance Packages' },
    { key: 'claimTypes', name: 'Claim Types' },
    { key: 'claimCauses', name: 'Claim Causes' },
    { key: 'bankCodes', name: 'Bank Codes' },
    { key: 'bankBranches', name: 'Bank Branches' }
  ];

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/defaults/sync/status');
      setSyncStatus(response.data.data);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    }
  }, []);

  // Fetch sync history
  const fetchSyncHistory = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      const response = await apiClient.get(`/defaults/sync/history?${params.toString()}`);
      
      setSyncHistory(response.data.data.history || []);
      
      // Update pagination
      const backendPagination = response.data.data.pagination;
      if (backendPagination) {
        setPagination({
          page: backendPagination.currentPage,
          limit: backendPagination.limit,
          total: backendPagination.totalCount,
          totalPages: backendPagination.totalPages
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch sync history');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    if (!syncStatus?.isActive) {
      setProgressPossiblyStale(false);
      progressStuckRef.current = { key: null, since: null };
      return;
    }
    const key = `${syncStatus.progress ?? 0}|${syncStatus.currentEndpoint ?? ''}`;
    if (progressStuckRef.current.key !== key) {
      progressStuckRef.current = { key, since: Date.now() };
      setProgressPossiblyStale(false);
    }
  }, [syncStatus?.isActive, syncStatus?.progress, syncStatus?.currentEndpoint]);

  useEffect(() => {
    if (!syncStatus?.isActive) return undefined;
    const tick = () => {
      const { key, since } = progressStuckRef.current;
      if (!since || !key) return;
      if (Date.now() - since > 9000) {
        setProgressPossiblyStale(true);
      }
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
  }, [syncStatus?.isActive, syncStatus?.progress, syncStatus?.currentEndpoint]);

  // Monitor sync progress and show completion/failure notifications using polling
  useEffect(() => {
    if (!syncStatus?.isActive) return;

    let subscription = null;
    let hasShownCompletionToast = false;

    try {
      // Import polling service dynamically
      import('../../services/slamsSyncService').then(({ subscribeSyncProgress }) => {
        subscription = subscribeSyncProgress((statusData) => {
          // Extract sync information from status data
          const { data, error } = statusData;
          
          if (error) {
            // Handle polling errors
            console.error('Sync progress monitoring error:', error);
            showWarning('Connection to sync progress lost', {
              title: 'Progress Monitoring',
              description: 'Real-time sync updates may not be available',
              duration: 6000
            });
            return;
          }
          
          // Update sync status for real-time progress bar updates
          if (data) {
            setSyncStatus(data);
          }
          
          if (data && !data.isActive && !hasShownCompletionToast) {
            // Sync completed or stopped
            hasShownCompletionToast = true;
            
            const completionToastId = showSuccess(`Sync completed successfully!`, {
              title: 'Sync Complete',
              description: 'All data synchronized successfully',
              duration: 8000,
              actions: [
                { label: 'View Results', type: 'view_results' },
                { label: 'View History', type: 'view_history' }
              ],
              showActions: true,
              data: {
                customActions: {
                  view_results: () => {
                    // Refresh data and show results
                    fetchSyncStatus();
                    fetchSyncHistory(1);
                  },
                  view_history: () => {
                    const historySection = document.getElementById('sync-history');
                    if (historySection) historySection.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }
            });
            
            // Refresh sync status and history after a short delay
            setTimeout(() => {
              fetchSyncStatus();
              fetchSyncHistory(1);
            }, 1000);
          }
        }, 3000); // Poll every 3 seconds
      });
    } catch (error) {
      console.error('Failed to setup sync progress monitoring:', error);
    }

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, [syncStatus?.isActive, showSuccess, showWarning, fetchSyncStatus, fetchSyncHistory]);



  // Initialize web components
  useEffect(() => {
    defineCustomElements();
  }, []);

  // Initialize data
  useEffect(() => {
    if (token) {
      fetchSyncStatus();
      fetchSyncHistory();
    }
  }, [token, fetchSyncStatus, fetchSyncHistory]);

  // Handle sync all endpoints
  const handleSyncAll = async () => {
    let loadingToastId = null;
    
    try {
      setLoading(true);
      setError(null);
      
      // Show loading toast
      loadingToastId = showLoading('Initiating full SLAMS sync...', {
        title: 'Full Sync',
        description: 'This will sync all 12 SLAMS endpoints',
        persistent: true
      });
      
      const response = await apiClient.post('/defaults/sync/all');
      const syncData = response.data.data;
      
      // Remove loading toast
      if (loadingToastId) removeToast(loadingToastId);
      
      // Show success toast with sync details
      const successToastId = showSuccess('Full sync initiated successfully!', {
        title: 'Sync Started',
        description: `Sync ID: ${syncData.syncId} - Estimated duration: ${syncData.estimatedDuration}`,
        duration: 8000,
        actions: [
          { label: 'View Progress', type: 'view_progress' },
          { label: 'View History', type: 'view_history' }
        ],
        showActions: true,
        data: {
          syncId: syncData.syncId,
          customActions: {
            view_progress: () => {
              // Scroll to progress section 
              const progressSection = document.getElementById('sync-progress');
              if (progressSection) {
                progressSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                // If no active sync, scroll to sync controls
                const syncControls = document.querySelector('[data-sync-controls]');
                if (syncControls) syncControls.scrollIntoView({ behavior: 'smooth' });
              }
            },
            view_history: () => {
              // Scroll to history section
              const historySection = document.getElementById('sync-history');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        }
      });
      
      // Track active sync toast
      setActiveSyncToasts(prev => new Map(prev.set('full_sync', successToastId)));
      
      // Refresh status after initiating sync
      setTimeout(() => {
        fetchSyncStatus();
        fetchSyncHistory(1);
      }, 1000);
      
    } catch (err) {
      // Remove loading toast if it exists
      if (loadingToastId) removeToast(loadingToastId);
      
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 'Failed to initiate full sync';
      const retryInfo = errorData?.details?.retryInfo;
      
      // Enhanced error toast with retry information
      showError(errorMessage, {
        title: 'Sync Failed',
        description: retryInfo?.canRetry ? 
          `You can retry ${retryInfo.retryStrategy === 'exponential_backoff' ? 'with exponential backoff' : 'after ' + retryInfo.retryAfterSeconds + ' seconds'}` :
          'Please check the error details and contact administrator if needed',
        duration: retryInfo?.canRetry ? 10000 : 15000,
        actions: retryInfo?.canRetry ? [
          { label: 'Retry Now', type: 'retry' },
          { label: 'View Details', type: 'view_details' }
        ] : [
          { label: 'View Details', type: 'view_details' },
          { label: 'Contact Support', type: 'contact_support' }
        ],
        showActions: true,
        data: {
          errorType: 'SYNC_INITIATION_ERROR',
          errorDetails: errorData?.details,
          retryCallback: retryInfo?.canRetry ? () => handleSyncAll() : null,
          detailsCallback: () => {
            console.log('Full error details:', errorData);
            // Could open a modal with full error details
          }
        }
      });
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle sync specific endpoint
  const handleSyncEndpoint = async (endpoint) => {
    let loadingToastId = null;
    
    try {
      setLoading(true);
      setError(null);
      
      // Find endpoint display name
      const endpointConfig = slamEndpoints.find(e => e.key === endpoint);
      const displayName = endpointConfig?.name || endpoint;
      
      // Show loading toast
      loadingToastId = showLoading(`Initiating ${displayName} sync...`, {
        title: 'Endpoint Sync',
        description: `Syncing ${displayName} data from SLAMS`,
        persistent: true
      });
      
      const response = await apiClient.post(`/defaults/sync/${endpoint}`);
      const syncData = response.data.data;
      
      // Remove loading toast
      if (loadingToastId) removeToast(loadingToastId);
      
      // Show success toast with sync details
      const successToastId = showSuccess(`${displayName} sync initiated successfully!`, {
        title: 'Endpoint Sync Started',
        description: `Sync ID: ${syncData.syncId} - Estimated duration: ${syncData.estimatedDuration}`,
        duration: 6000,
        actions: [
          { label: 'View Progress', type: 'view_progress' },
          { label: 'View History', type: 'view_history' }
        ],
        showActions: true,
        data: {
          syncId: syncData.syncId,
          endpoint: endpoint,
          customActions: {
            view_progress: () => {
              const progressSection = document.getElementById('sync-progress');
              if (progressSection) {
                progressSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                // If no active sync, scroll to sync controls
                const syncControls = document.querySelector('[data-sync-controls]');
                if (syncControls) syncControls.scrollIntoView({ behavior: 'smooth' });
              }
            },
            view_history: () => {
              const historySection = document.getElementById('sync-history');
              if (historySection) {
                historySection.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        }
      });
      
      // Track active sync toast
      setActiveSyncToasts(prev => new Map(prev.set(endpoint, successToastId)));
      
      // Refresh status after initiating sync
      setTimeout(() => {
        fetchSyncStatus();
        fetchSyncHistory(1);
      }, 1000);
      
    } catch (err) {
      // Remove loading toast if it exists
      if (loadingToastId) removeToast(loadingToastId);
      
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || `Failed to sync ${endpoint}`;
      const retryInfo = errorData?.details?.retryInfo;
      const displayName = slamEndpoints.find(e => e.key === endpoint)?.name || endpoint;
      
      // Enhanced error toast with retry information
      showError(errorMessage, {
        title: `${displayName} Sync Failed`,
        description: retryInfo?.canRetry ? 
          `You can retry ${retryInfo.retryStrategy === 'exponential_backoff' ? 'with exponential backoff' : 'after ' + retryInfo.retryAfterSeconds + ' seconds'}` :
          'Please check the error details and contact administrator if needed',
        duration: retryInfo?.canRetry ? 8000 : 12000,
        actions: retryInfo?.canRetry ? [
          { label: 'Retry Now', type: 'retry' },
          { label: 'View Details', type: 'view_details' }
        ] : [
          { label: 'View Details', type: 'view_details' },
          { label: 'Contact Support', type: 'contact_support' }
        ],
        showActions: true,
        data: {
          errorType: 'ENDPOINT_SYNC_ERROR',
          endpoint: endpoint,
          errorDetails: errorData?.details,
          retryCallback: retryInfo?.canRetry ? () => handleSyncEndpoint(endpoint) : null,
          detailsCallback: () => {
            console.log(`${endpoint} sync error details:`, errorData);
            // Could open a modal with full error details
          }
        }
      });
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel sync
  const handleCancelSync = async () => {
    let loadingToastId = null;
    
    try {
      setLoading(true);
      setError(null);
      
      // Show loading toast
      loadingToastId = showLoading('Cancelling sync operation...', {
        title: 'Cancel Sync',
        description: 'Attempting to stop the running sync operation',
        persistent: true
      });
      
      const response = await apiClient.delete('/defaults/sync/cancel');
      const cancelData = response.data;
      
      // Remove loading toast
      if (loadingToastId) removeToast(loadingToastId);
      
      // Clear any active sync toasts
      activeSyncToasts.forEach((toastId) => {
        removeToast(toastId);
      });
      setActiveSyncToasts(new Map());
      
      // Show success toast
      showSuccess('Sync operation cancelled successfully!', {
        title: 'Sync Cancelled',
        description: cancelData?.message || 'The running sync operation has been stopped',
        duration: 5000,
        actions: [
          { label: 'View History', type: 'view_history' }
        ],
        showActions: true,
        data: {
          customActions: {
            view_history: () => {
              const historySection = document.getElementById('sync-history');
              if (historySection) historySection.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
      });
      
      // Refresh status
      setTimeout(() => {
        fetchSyncStatus();
        fetchSyncHistory(1);
      }, 1000);
      
    } catch (err) {
      // Remove loading toast if it exists
      if (loadingToastId) removeToast(loadingToastId);
      
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 'Failed to cancel sync';
      
      // Show error toast
      showError(errorMessage, {
        title: 'Cancel Failed',
        description: 'Could not cancel the running sync operation',
        duration: 8000,
        actions: [
          { label: 'Retry Cancel', type: 'retry' },
          { label: 'View Details', type: 'view_details' }
        ],
        showActions: true,
        data: {
          errorType: 'SYNC_CANCEL_ERROR',
          errorDetails: errorData?.details,
          retryCallback: () => handleCancelSync(),
          detailsCallback: () => {
            console.log('Cancel sync error details:', errorData);
          }
        }
      });
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchSyncHistory(newPage);
  };

  // Format date time
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Get status tag type for omk-tag component
  const getStatusTagType = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
        return 'positive';
      case 'failed':
      case 'error':
        return 'danger';
      case 'in_progress':
      case 'running':
        return 'warning';
      default:
        return 'information';
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-6">
        <BackButton />
      </div>

      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">SLAMS Sync Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage synchronization of defaults data from SLAMS API
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <OmAlert 
          alertType="error" 
          alertTitle="Error"
          message={error}
          className="mb-4"
        />
      )}

      {/* Sync Controls */}
      <OmCard className="p-6" data-sync-controls>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Sync Controls</h2>
        </div>
        
        <div className="space-y-4">
          {/* Full Sync Button */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Full Sync</h3>
              <p className="text-sm text-gray-600">
                Synchronize all SLAMS endpoints to update defaults data
              </p>
            </div>
            <OmButton
              buttonType="filled-button"
              buttonSize="medium"
              leadingIcon="sync"
              onClick={handleSyncAll}
              disabled={loading || syncStatus?.isActive}
            >
              Sync All
            </OmButton>
          </div>

          {/* Cancel Sync Button */}
          {syncStatus?.isActive && (
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-red-900">Cancel Sync</h3>
                <p className="text-sm text-red-600">
                  Stop the currently running sync operation
                </p>
              </div>
              <OmButton
                buttonType="outlined-button"
                buttonSize="medium"
                leadingIcon="stop"
                onClick={handleCancelSync}
                disabled={loading}
              >
                Cancel Sync
              </OmButton>
            </div>
          )}

          {/* Progress Bar */}
          {syncStatus?.isActive && (
            <div id="sync-progress" className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>
                  Progress:{' '}
                  {progressPossiblyStale
                    ? 'Syncing…'
                    : (syncStatus.currentEndpoint || 'Starting...')}
                </span>
                <span>
                  {progressPossiblyStale
                    ? '—'
                    : `${syncStatus?.progress ? Math.round(syncStatus.progress) : 0}%`}
                </span>
              </div>
              <OmLinearProgress
                value={progressPossiblyStale ? 0 : (syncStatus?.progress || 0)}
                max={100}
                indeterminate={progressPossiblyStale}
                className="w-full"
              />
            </div>
          )}
        </div>
      </OmCard>

      {/* Individual Endpoint Sync */}
      <OmCard className="p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Individual Endpoint Sync</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {slamEndpoints.map((endpoint) => (
            <div 
              key={endpoint.key}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <span className="text-sm font-medium text-gray-900">
                {endpoint.name}
              </span>
              <OmButton
                buttonType="outlined-button"
                buttonSize="small"
                leadingIcon="sync"
                onClick={() => handleSyncEndpoint(endpoint.key)}
                disabled={loading || syncStatus?.isActive}
              >
                Sync
              </OmButton>
            </div>
          ))}
        </div>
      </OmCard>

      {/* Sync History */}
      <OmCard id="sync-history" className="p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sync History</h2>
        
        {loading && syncHistory.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-gray-600">Loading sync history...</div>
          </div>
        ) : syncHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No sync history available
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {syncHistory.map((record, index) => (
                    <tr key={record.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(record.startedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.syncType || 'Manual'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.endpoint || 'All'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <omk-tag type={getStatusTagType(record.status)}>
                          {record.status}
                        </omk-tag>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.recordsProcessed || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.duration ? `${Math.round(record.duration)}s` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <OmPagination
              currentPage={pagination.page}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={handlePageChange}
              showResultsInfo={true}
            />
          </>
        )}
      </OmCard>
    </div>
  );
};

export default SlamsSyncManagement;